import { ref, toRefs } from 'vue';
import { defineStore } from 'pinia';
import { type Pagination } from '@/types/api/response.types';
import {
  CreditRequest,
  CreditRequestPaginationParams,
  CreditRequestSite,
  CreditRequestSiteParams,
  CreditRequestTableItem,
} from '@/types/api/credit-requests.type';
import { selectedOrgId } from '@/common/utils/role.utils';
import {
  getAllCreditRequests,
  getCreditRequestCount,
  getCreditRequestCreatedBy,
  getCreditRequestDetails,
  getCreditRequestDoc,
  getCreditRequestSites,
  getCreditRequestStatuses,
  updateCreditRequestStatus,
  uploadProofOfCredit,
} from '@/api/credit-requests.service';
import { ReturnCreatedBy, ReturnStatusUpdateBody } from '@/types/api/returns.types';
import { DocumentUploadResponse } from '@/types/api/orders.types';
import { useTotalsStore } from '@/stores/utils/totals.store';
import { documentUploadToast, viewFetchedDocument } from '@/common/utils/general.utils';
import i18n from '@/plugins/vue-i18n';
import { useToastStore } from '@/stores/utils/toast.store';

const { t } = i18n.global;

export const useCreditRequestStore = defineStore('credit-requests', () => {
  const { creditsByOrgCount, creditsByVendorCount } = toRefs(useTotalsStore());

  const statuses = ref<string[] | null>(null);
  const completeCount = ref<number | null>(null);
  const cancelledCount = ref<number | null>(null);
  const sites = ref<CreditRequestSite[] | null>(null);
  const createdByList = ref<ReturnCreatedBy[] | null>(null);
  const creditRequests = ref<CreditRequestTableItem[] | null>(null);
  const creditRequestDetails = ref<CreditRequest | null>(null);
  const creditRequestDoc = ref<DocumentUploadResponse | null>(null);
  const receivedAmount = ref<number | null>(null);

  const viewingOrderFromCredit = ref(false);
  const viewingReturnFromCredit = ref(false);
  const viewingPRFromCredit = ref(false);

  const isLoading = ref(true);
  const isLoadingDoc = ref(true);
  const isLoadingDetails = ref(true);

  const sitesFilters = ref<CreditRequestSiteParams>({
    orgId: selectedOrgId.value!,
    accountsPayableId: '',
  });

  const filters = ref<CreditRequestPaginationParams>({
    organisationId: selectedOrgId.value!,
    pageNo: 1,
    pageSize: 20,
  });
  const pagination = ref<Pagination | null>(null);

  const fetchCreditRequests = async () => {
    try {
      isLoading.value = true;

      const requests = await getAllCreditRequests(filters.value);

      creditRequests.value = requests.data.data;
      pagination.value = requests.data.meta.pagination;
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
  };

  // Hosts open one credit after another over the same slot, so an earlier read answering last
  // must not put its credit on screen: the latest read wins.
  let latestDetailsRequest = 0;

  const fetchCreditRequestDetails = async (id: string) => {
    const request = ++latestDetailsRequest;

    try {
      isLoadingDetails.value = true;

      const response = await getCreditRequestDetails(id);

      if (request === latestDetailsRequest) creditRequestDetails.value = response.data.data;
    } catch (error) {
      console.error(error);
    } finally {
      if (request === latestDetailsRequest) isLoadingDetails.value = false;
    }
  };

  const uploadDoc = async (id: string, data: FormData, isCredit: boolean) => {
    const toastStore = useToastStore();

    try {
      const response = await uploadProofOfCredit(id, data);
      documentUploadToast({
        responseCode: response.status,
        title: t('accounts.creditRequests.toasts.uploadDocSuccess.title', {
          type: t(`accounts.creditRequests.${isCredit ? 'credit' : 'refund'}`),
        }),
        description: t('accounts.creditRequests.toasts.uploadDocSuccess.content', {
          type: t(`accounts.creditRequests.${isCredit ? 'credit' : 'refund'}`),
        }),
      });
    } catch (error) {
      console.error('Error uploading document:', error);
      toastStore.showToast(
        'error',
        t('accounts.creditRequests.toasts.uploadDocError.title', {
          type: t(`accounts.creditRequests.${isCredit ? 'credit' : 'refund'}`),
        }),
        t('accounts.creditRequests.toasts.uploadDocError.content', {
          type: t(`accounts.creditRequests.${isCredit ? 'credit' : 'refund'}`),
        }),
      );
    }
  };

  const fetchCreditRequestDoc = async (id: string, documentId: string) => {
    try {
      isLoadingDoc.value = true;

      await viewFetchedDocument(async () => {
        const doc = await getCreditRequestDoc(id, documentId);
        creditRequestDoc.value = doc.data.data;
        return creditRequestDoc.value.url;
      });
    } catch (error) {
      console.error(error);
    } finally {
      isLoadingDoc.value = false;
    }
  };

  const fetchCreatedByList = async () => {
    try {
      const createdBy = await getCreditRequestCreatedBy(sitesFilters.value);

      createdByList.value = createdBy.data.data;
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchSitesList = async () => {
    try {
      const creditRequestsSites = await getCreditRequestSites(sitesFilters.value);

      sites.value = creditRequestsSites.data.data;
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchCount = async () => {
    try {
      const creditRequestsCount = await getCreditRequestCount(filters.value);

      // Route the pending count to the org- or vendor-scoped total depending on
      // whether the fetch is account-scoped (Account Details) or org-scoped (the
      // accounts-view Credits tab) — mirrors the org/vendor split used for PRs.
      if (filters.value.accountsPayableId) {
        creditsByVendorCount.value = creditRequestsCount.data.data.pending;
      } else {
        creditsByOrgCount.value = creditRequestsCount.data.data.pending;
      }
      completeCount.value = creditRequestsCount.data.data.complete;
      cancelledCount.value = creditRequestsCount.data.data.cancelled;
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
  };

  const updateStatus = async (id: string, body: ReturnStatusUpdateBody) => {
    try {
      const response = await updateCreditRequestStatus(id, body);

      if (response.status === 200) {
        creditRequestDetails.value = response.data.data;
        let matchFound = false;

        creditRequests.value =
          creditRequests.value?.map((item) => {
            if (item.id === id) {
              matchFound = true;
              return { ...item, status: body.status };
            }

            return item;
          }) ?? null;

        if (!matchFound) {
          console.warn(`Credit Request ID ${id} not found in credit requests list.`);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
  };
  const $reset = () => {
    sites.value = null;
    statuses.value = null;
    createdByList.value = null;
    creditRequests.value = null;
    creditRequestDoc.value = null;
    creditRequestDetails.value = null;

    completeCount.value = null;
    cancelledCount.value = null;

    viewingOrderFromCredit.value = false;
    viewingReturnFromCredit.value = false;

    isLoading.value = true;
    isLoadingDoc.value = true;
    isLoadingDetails.value = true;

    sitesFilters.value = {
      orgId: '',
      accountsPayableId: '',
    };
    filters.value = {
      organisationId: '',
      pageNo: 1,
      pageSize: 20,
    };
    pagination.value = null;
  };

  const init = async () => {
    try {
      if (!statuses.value?.length) {
        const creditRequestStatuses = await getCreditRequestStatuses();
        statuses.value = creditRequestStatuses.data.data;
      }
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    sites,
    filters,
    statuses,
    isLoading,
    isLoadingDoc,
    isLoadingDetails,
    pagination,
    sitesFilters,
    createdByList,
    creditRequests,
    creditRequestDoc,
    creditRequestDetails,
    completeCount,
    cancelledCount,
    viewingOrderFromCredit,
    viewingReturnFromCredit,
    viewingPRFromCredit,
    receivedAmount,
    $reset,
    fetchCount,
    fetchSitesList,
    fetchCreatedByList,
    fetchCreditRequests,
    fetchCreditRequestDoc,
    fetchCreditRequestDetails,
    updateStatus,
    uploadDoc,
    init,
  };
});
